import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Form, Input, Radio, Row } from 'antd'

import B2becCustomSelect from '@/components/B2becCustomSelect'
import AddressSelect from '@/components/DeliveryAddressBlock'
import { useAppSelector } from '@/store/hook'
import {
  selectAddressScheme,
  selectCountryNames,
  selectShouldHideHouseNumberEntryField,
} from '@/store/selectors/configSelector'
import { selectDeliveryAddresses } from '@/store/slices/customerSlice'
import useRegion from '@/hooks/useRegion'
import { ADDRESS_SCHEME_TYPES } from '@/libs/constants/configs'
import { validateZipCode } from '@/libs/utils/validatorUtils'

const CustomAddressSelect = (props) => {
  const { id, onChange } = props
  const [t] = useTranslation()

  const [isNewAddress, setIsNewAddress] = useState(false)
  const [forceRender, setForceRender] = useState()

  const rawDeliveryAddresses = useAppSelector(selectDeliveryAddresses)
  const countryNamesConfig = useAppSelector(selectCountryNames)
  const shouldHideHouseNumber = useAppSelector(
    selectShouldHideHouseNumberEntryField,
  )
  const addressScheme = useAppSelector(selectAddressScheme)
  const isUSAAddressScheme = addressScheme === ADDRESS_SCHEME_TYPES.USA

  const { isShowRegion, regions } = useRegion()

  const addresses = useMemo(() => {
    return rawDeliveryAddresses.map(({ region, ...addr }) => {
      const foundCountryName = countryNamesConfig.find(
        (config) => config.value === addr.country,
      )

      if (isShowRegion) {
        return {
          ...addr,
          country: foundCountryName ? foundCountryName.value : addr.country,
          region,
        }
      }
      return addr
    })
  }, [rawDeliveryAddresses, countryNamesConfig, isShowRegion])

  const handleSelectAddress = useCallback(
    (value) => {
      onChange(value)
      setIsNewAddress(false)
    },
    [onChange],
  )

  const handleToggleNewAddress = useCallback(() => {
    let defaultCountry = ''
    if (addresses && addresses.length > 0) {
      defaultCountry = addresses[0].country
    }

    const foundCountryName = countryNamesConfig.find(
      (config) => config.value === defaultCountry,
    )

    onChange({
      country: foundCountryName ? foundCountryName.name : defaultCountry,
    })
    setForceRender(Math.random())
    setIsNewAddress(true)
  }, [addresses, countryNamesConfig, onChange])

  return (
    <Row gutter={[12, 12]}>
      <Col span={24} className="mb-3">
        <AddressSelect
          key={forceRender}
          deliveryAddresses={addresses}
          placeholder={t('rental.modal.locationMachine')}
          onChange={handleSelectAddress}
        />
      </Col>
      <Col span={24}>
        <Radio checked={isNewAddress} onClick={handleToggleNewAddress}>
          {t('form.label.newAddress')}
        </Radio>
      </Col>
      {isNewAddress && (
        <>
          <Col flex="auto">
            <Form.Item
              className="custom-form-item"
              label={t('form.label.street')}
              name={[id, 'street']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          {!shouldHideHouseNumber && (
            <Col span={12}>
              <Form.Item
                className="custom-form-item"
                label={t('form.label.houseNumber')}
                name={[id, 'houseNumber']}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.street4')}
              name={[id, 'street4']}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 8 }} order={isUSAAddressScheme ? 3 : 1} span={24}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.zip')}
              name={[id, 'zip']}
              rules={[
                { required: true },
                ({ getFieldValue }) => {
                  const country = getFieldValue([id, 'country'])
                  return validateZipCode(country)
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          {isShowRegion && (
            <Col lg={{ span: 8 }} order={2} span={24}>
              <Form.Item
                className="custom-form-item"
                label={t('form.label.region')}
                name={[id, 'region']}
                rules={[{ required: true }]}
              >
                <B2becCustomSelect
                  options={regions}
                  placeholder={t('form.placeHolder.region')}
                />
              </Form.Item>
            </Col>
          )}
          <Col
            flex="auto"
            lg={{ span: isShowRegion ? 8 : 'auto' }}
            order={isUSAAddressScheme ? 1 : 3}
          >
            <Form.Item
              className="custom-form-item"
              label={t('form.label.city')}
              name={[id, 'city']}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              className="custom-form-item"
              label={t('form.label.country')}
              name={[id, 'country']}
            >
              <Input disabled />
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
  )
}

export default CustomAddressSelect
